import { useEffect } from "react";
import { Admin, Layout, Resource, CustomRoutes } from "react-admin";
import { Route, Navigate, useLocation } from "react-router-dom";
import authProvider from "../components/authProvider";
import LoginPage from "../components/LoginPage";
import {
  OrdersList,
} from "../components/ItemsList";
// Legacy components - deprecated in favor of HomeLayoutManager
// import { BannersList, BannersItemsList } from "../components/ItemsList";
// import { CategoriesList, CategoryEdit, CategoryCreate } from "../components/CategoryComponents";
// import ShowBannerItem from "../components/showBannerItem";
// import CreateBanner from "../components/createBanner";
// import CreateBannerItems from "../components/createBannerItems";
// import ShowSingleBannerItems from "../components/showSingleBannerItems";
import customDataProvider from "../components/fetchApiData";
import ShowOrderItem from "../components/showOrderItem";
import CustomAppBar from "../components/CustomAppBar";
import {
  WebhookEndpointList,
  WebhookEndpointCreate,
  WebhookEndpointEdit,
} from "../components/WebhookEndpoints";
import {
  WebhookDispatchList,
  WebhookDispatchShow,
} from "../components/WebhookDispatches";
import { StoreWorkingHoursEdit } from "../components/StoreWorkingHoursEdit";
import { StoreEarlyCutoffEdit } from "../components/StoreEarlyCutoffEdit";
// import { CategoryEarlyCutoffSelection } from "../components/CategoryEarlyCutoffSelection"; // Deprecated - now inline in CategoryManagement
import { StoreSelector } from "../components/StoreSelector";
import { SystemDefaultsEdit } from "../components/SystemDefaultsEdit";
import { StoreSettingsEdit } from "../components/StoreSettingsEdit";
import SyncControl from "../components/SyncControl";
import DevMenu from "../components/DevMenu";
import OrderManagerTablets from "../pages/OrderManagerTablets";
import CustomMenu from "../components/CustomMenu";
// StoreInfo removed - replaced by StoreCommandCenter
// PaymentGatewaySettings, DearSettings, AppSettings - now unified in SystemCore
import SystemCore from "../pages/SystemCore";
// Old standalone pages - now unified in Content Control (HomeLayoutManager)
// import BannerManagement from "../components/BannerManagement";
// import CategoryManagement from "../components/CategoryManagement";
import StoreMessagesManager from "../components/StoreMessages";
import ContentControl from "../pages/HomeLayoutManager"; // Renamed from HomeLayoutManager
import StoreCommandCenter from "../pages/StoreCommandCenter";
import VaperiteStores from "../pages/VaperiteStores";
import DiscountsPage from "../pages/Discounts";
import UserManagement from "../components/UserManagement";
import MetricsDashboard from "../pages/MetricsDashboard";
import Dashboard from "../pages/Dashboard";
import ProductReview from "../pages/ProductReview";
import BarcodeAudit from "../pages/BarcodeAudit";
import VaperiteStagingDashboard from "../pages/VaperiteStagingDashboard";
import UberPromotions from "../pages/UberPromotions";
import UberOrders from "../pages/UberOrders";
import UberDeliveries from "../pages/UberDeliveries";
import WebhookTestingTool from "../pages/WebhookTestingTool";
import ToolsPage from "../pages/Tools";
import UberMenu from "../pages/UberMenu";
import UberToolsPage from "../pages/UberTools";
import ProductManagement from "../pages/ProductManagement";
import MarketingSEO from "../pages/MarketingSEO";
import InventoryDashboard from "../pages/InventoryDashboard";
import MissionBuilder from "../pages/MissionBuilder";
import LiveOrders from "../pages/LiveOrders";
import TFSDeliveries from "../pages/TFSDeliveries";
import Customers from "../pages/Customers";
import CustomerMapPage from "../pages/CustomerMapPage";
import NewCustomers from "../pages/NewCustomers";
import StaffUsers from "../pages/StaffUsers";
import { createDeeliverTheme } from "../theme";
import { useThemeMode } from "../context/ThemeModeContext";
import { StoreScopeProvider } from "../context/StoreScopeContext";
import CustomNotification from "../components/CustomNotification";
import PageGate from "../components/PageGate";

/**
 * App layout. Resets scroll on navigation; everything else is react-admin's.
 */
const MyLayout = (props: any) => {
  const location = useLocation();

  // Reset scroll to top on every route change — react-admin <Layout> + MenuItemLink
  // SPA nav doesn't reset scroll, so pages otherwise open mid-scroll.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Layout {...props} appBar={CustomAppBar} menu={CustomMenu}>
      <PageGate>{props.children}</PageGate>
    </Layout>
  );
};

const App = () => {
  // Build the theme from the current ground (see theme/grounds.ts)
  const { ground } = useThemeMode();
  const theme = createDeeliverTheme(ground);

  return (
    // Store scope lives above <Admin> so the app-bar picker and the scoped pages
    // (Live Orders) read the same selection.
    <StoreScopeProvider>
    <Admin
      authProvider={authProvider}
      dataProvider={customDataProvider}
      layout={MyLayout}
      theme={theme}
      loginPage={LoginPage}
      notification={CustomNotification}
      dashboard={Dashboard}
    >
        <Resource name="orders" list={OrdersList} edit={ShowOrderItem} />
        {/* Banner resources kept for API compatibility but redirected to Content Control */}
        <Resource name="banners" />
        <Resource name="banner-items" />
        {/* Category resource kept for API compatibility but redirected to Content Control */}
        <Resource name="categories" />
        {/* store-settings removed - replaced by Store Command Center */}
        <Resource
          name="webhook-endpoints"
          list={WebhookEndpointList}
          edit={WebhookEndpointEdit}
          create={WebhookEndpointCreate}
        />
        <Resource
          name="webhook-dispatches"
          list={WebhookDispatchList}
          show={WebhookDispatchShow}
          options={{ label: "Webhook Dispatches" }}
        />
    <CustomRoutes>
      {/* Live Orders - always available, no role restriction */}
      <Route path="/live-orders" element={<LiveOrders />} />

          {/* Content Control - Unified Banners + Categories with Draft/Publish */}
          <Route path="/content/control" element={<ContentControl />} />
          {/* Redirect old /layout/home to Content Control */}
          <Route path="/layout/home" element={<Navigate to="/content/control" replace />} />

          {/* Redirect old banner routes to Content Control */}
          <Route path="/banners" element={<Navigate to="/content/control" replace />} />
          <Route path="/banners/unified" element={<Navigate to="/content/control" replace />} />
          <Route path="/banners/:id" element={<Navigate to="/content/control" replace />} />
          <Route path="/banners/:id/show" element={<Navigate to="/content/control" replace />} />
          <Route path="/banner-items" element={<Navigate to="/content/control" replace />} />
          <Route path="/banner-items/:id" element={<Navigate to="/content/control" replace />} />
          <Route path="/banner-items/:id/show" element={<Navigate to="/content/control" replace />} />

          {/* Redirect old category routes to Content Control */}
          <Route path="/categories" element={<Navigate to="/content/control" replace />} />
          <Route path="/categories/:id" element={<Navigate to="/content/control" replace />} />
          <Route path="/categories/:id/show" element={<Navigate to="/content/control" replace />} />
          <Route path="/categories/create" element={<Navigate to="/content/control" replace />} />
          <Route path="/categories/cutoffs" element={<Navigate to="/content/control" replace />} />
          {/* Redirect store-settings to Store Command Center */}
          <Route path="/store-settings" element={<Navigate to="/stores/command-center" replace />} />

          <Route path="/stores" element={<StoreSelector />} />
          <Route path="/vaperite/stores" element={<VaperiteStores />} />
          <Route path="/stores/command-center" element={<StoreCommandCenter />} />
          <Route path="/stores/defaults" element={<SystemDefaultsEdit />} />
          <Route path="/stores/:id/settings" element={<StoreSettingsEdit />} />
          <Route path="/stores/:id/hours" element={<StoreWorkingHoursEdit />} />
          <Route path="/stores/:id/cutoffs" element={<StoreEarlyCutoffEdit />} />
          {/* CategoryEarlyCutoffSelection deprecated - now redirects to unified CategoryManagement above */}
          <Route path="/admin-functions/sync-control" element={<SyncControl />} />
          <Route path="/vaperite/sync" element={<SyncControl variant="vaperite" />} />
          <Route path="/deeliver/sync" element={<SyncControl variant="deeliver" />} />
          {/* StoreInfo removed - replaced by StoreCommandCenter at /stores/command-center */}
          <Route path="/admin-functions/dev-menu" element={<DevMenu />} />
          <Route path="/vaperite/order-tablets" element={<OrderManagerTablets />} />
          <Route path="/admin-functions/order-manager-tablets" element={<Navigate to="/vaperite/order-tablets" replace />} />
          {/* System Core - Unified Payment, Inventory, and App settings */}
          <Route path="/settings/system-core" element={<SystemCore />} />
          {/* Redirect old settings routes to System Core */}
          <Route path="/settings/payment-gateways" element={<Navigate to="/settings/system-core" replace />} />
          <Route path="/settings/dear" element={<Navigate to="/settings/system-core" replace />} />
          <Route path="/settings/app" element={<Navigate to="/settings/system-core" replace />} />
          <Route path="/messages" element={<StoreMessagesManager />} />

          {/* Marketing - Promotions & Rewards */}
          {/* Marketing & SEO Dashboard */}
          <Route path="/marketing/seo" element={<MarketingSEO />} />

          {/* Discount Engine (phase 6b) */}
          {/* One splat route: the page owns its sub-routes (/discounts/promotions,
              /discounts/promotions/:id, /discounts/customers, /discounts/customers/:id,
              /discounts/loyalty, /discounts/referrals) and redirects bare /discounts. */}
          <Route path="/discounts/*" element={<DiscountsPage />} />

          {/* User Management split into Customers (own section) + Staff Users (Admin Tools).
              Old combined route kept as a redirect for existing bookmarks. */}
          <Route path="/admin/user-management" element={<Navigate to="/customers" replace />} />
          <Route path="/customers" element={<Customers />} />
          {/* Where the dashboard's "New Customer Information -> View all" lands:
              the latest signups as full profile cards (Command Center parity). */}
          <Route path="/customers/new" element={<NewCustomers />} />
          <Route path="/admin/staff" element={<StaffUsers />} />

          {/* Redirects for old barcode review/audit bookmarks → Inventory Tools */}
          <Route path="/operations/inventory" element={<Navigate to="/tools" replace />} />
          <Route path="/operations/product-review" element={<Navigate to="/tools" replace />} />
          <Route path="/operations/barcode-audit" element={<Navigate to="/tools" replace />} />

          {/* Business Stats merged into the default Dashboard ("/") — keep redirect for old bookmarks */}
          <Route path="/admin/stats" element={<Navigate to="/" replace />} />


          {/* System Settings (feature flags, env config) */}
          <Route path="/admin/metrics" element={<MetricsDashboard />} />

          {/* Vaperite Item Staging — the Vend product review queue */}
          <Route path="/vaperite/staging" element={<VaperiteStagingDashboard key="vaperite" />} />
          {/* Deeliver's own item staging: same dashboard, org read from the route (pages/vaperite/stagingOrg.ts).
              The keys make switching between the two remount the page; without them React keeps the
              Vaperite instance, its state and its data, and only another section clears it. */}
          <Route path="/deeliver/staging" element={<VaperiteStagingDashboard key="deeliver" />} />

          {/* Uber Eats Promotion Management */}
          <Route path="/uber-promotions" element={<UberPromotions />} />

          {/* Uber Orders Dashboard (real-time) */}
          <Route path="/uber-orders" element={<UberOrders />} />
          <Route path="/uber-menu" element={<UberMenu />} />
          <Route path="/uber-tools" element={<UberToolsPage />} />

          {/* Uber Direct Deliveries Dashboard */}
          <Route path="/uber-deliveries" element={<UberDeliveries />} />

          {/* TFS Deliveries (placeholder — integration pending) */}
          <Route path="/tfs-deliveries" element={<TFSDeliveries />} />


          {/* Product Management (catalogue products, stock, Uber) */}
          <Route path="/products" element={<ProductManagement vendOrg="deeliver" />} />
          {/* Same tool, scoped to the partner's catalogue. A filtered view rather
              than a second page: the DB-only vs pushed-to-Vend rules live in one
              component, so the two cannot drift apart. */}
          <Route path="/vaperite/products" element={<ProductManagement vendOrg="vaperite" />} />

          {/* Inventory Tools (SKU Generator, Negative Stock Fix) */}
          <Route path="/tools" element={<ToolsPage />} />

          {/* Mission Builder (configurable store tasks for pickers) */}
          <Route path="/missions" element={<MissionBuilder />} />

          {/* Customer Map — was an overlay behind a button on the Customers page */}
          <Route path="/customer-map" element={<CustomerMapPage />} />

          {/* Product Vetting Dashboard (companion-app field scan review) */}

          {/* Webhook Testing Tool (staging/dev only) */}
          <Route path="/integrations/webhook-testing" element={<WebhookTestingTool />} />
    </CustomRoutes>
  </Admin>
    </StoreScopeProvider>
  );
};

export default App;
